import { type ClassValue, clsx } from "clsx";
import type { KeyboardEvent } from "react";
import { twMerge } from "tailwind-merge";

import { CountryCode } from "../types/common";

export interface ICustomerServiceOpenStatus {
  openNow: boolean;
  /** Always the configured time range regardless of day (e.g. "(09 - 17)") */
  openHours: string;
}

const WEEKDAY_TO_NUMBER: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

/** SE: Mon–Fri 09–17, FI: Mon–Fri 10–16. Timezone: Europe/Stockholm (SE), Europe/Helsinki (FI). */
const CUSTOMER_SERVICE_HOURS: Record<
  CountryCode,
  { openHours: string; start: number; end: number; timeZone: string }
> = {
  [CountryCode.SE]: {
    openHours: "(09 - 17)",
    start: 9,
    end: 17,
    timeZone: "Europe/Stockholm",
  },
  [CountryCode.FI]: {
    openHours: "(10 - 16)",
    start: 10,
    end: 16,
    timeZone: "Europe/Helsinki",
  },
};

/**
 * Returns current customer service open/closed status for the given country.
 * Uses the country's timezone (Europe/Stockholm for SE, Europe/Helsinki for FI).
 */
export const getCustomerServiceOpenStatus = (
  countryCode: CountryCode | undefined,
): ICustomerServiceOpenStatus => {
  if (!countryCode) {
    throw new Error("Country code is required");
  }
  const config = CUSTOMER_SERVICE_HOURS[countryCode];
  const formatter = new Intl.DateTimeFormat("en", {
    timeZone: config.timeZone,
    hour: "numeric",
    hour12: false,
    weekday: "short",
  });
  const parts = formatter.formatToParts(new Date());
  const hour = Number(parts.find((p) => p.type === "hour")?.value ?? "0");
  const weekday = parts.find((p) => p.type === "weekday")?.value ?? "Sun";
  const day = WEEKDAY_TO_NUMBER[weekday] ?? 0;

  const isWeekday = day > 0 && day < 6;
  const openNow = isWeekday && hour >= config.start && hour < config.end;

  return { openNow, openHours: config.openHours };
};

export const getCompanyBrandName = (countryCode: CountryCode): string => {
  switch (countryCode) {
    case CountryCode.FI:
      return "Kredita";
    case CountryCode.SE:
      return "Krea";
    default:
      return "";
  }
};

export { amountValuesList } from "./amountValuesList";
export { formatUploadedAt } from "./formatUploadedAt";
export { isLimitedCompany } from "./isLimitedCompany";
export {
  type IResolveCalendarLinkParams,
  resolveCalendarLink,
} from "./resolveCalendarLink";

export const cn = (...inputs: ClassValue[]) => {
  return twMerge(clsx(inputs));
};

export const isHotkey = (hotkey: string, event: KeyboardEvent) => {
  const hotkeyPressed = hotkey.split("+");

  const isHotkeyPressed = hotkeyPressed.every((key) => {
    if (key === "mod") {
      const metaKey = event.metaKey;

      return metaKey;
    }
    if (key === "shift") {
      const shiftKey = event.shiftKey;

      return shiftKey;
    }
    return event.key === key;
  });

  return isHotkeyPressed;
};
